/**
 * Global authentication context for developer portal.
 *
 * Uses Amazon Cognito for authentication. Supports:
 *   - Email/password sign-in
 *   - Force password change on first login
 *   - Session restoration from Cognito's cached refresh tokens
 *   - Admin role detection (via Cognito groups)
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { Developer } from '../types';
import * as authService from '../services/auth.service';

interface AuthContextValue {
  developer: Developer | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  needsNewPassword: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  completeNewPassword: (newPassword: string) => Promise<boolean>;
  logout: () => void;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [developer, setDeveloper] = useState<Developer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsNewPassword, setNeedsNewPassword] = useState(false);

  // Restore session on mount
  useEffect(() => {
    authService
      .restoreSession()
      .then((dev) => {
        if (dev) setDeveloper(dev);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));

    // Listen for auth:expired events from the API client
    const handleExpired = () => {
      authService.logout();
      setDeveloper(null);
      setError('Your session has expired. Please sign in again.');
    };
    window.addEventListener('auth:expired', handleExpired);
    return () => window.removeEventListener('auth:expired', handleExpired);
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    setNeedsNewPassword(false);
    try {
      const result = await authService.login(email, password);

      if (!result.success) {
        setError(result.error || 'Login failed');
        return false;
      }

      if (result.needsNewPassword) {
        setNeedsNewPassword(true);
        return true; // Caller should show the new-password form
      }

      // Admin users may not have a DynamoDB developer record.
      // Check admin FIRST to avoid a /portal/me 401 that triggers auth:expired.
      if (authService.isAdmin()) {
        const stub = authService.getAdminStubDeveloper();
        if (stub) {
          setDeveloper(stub);
          return true;
        }
      }

      // Regular developer — fetch profile from API
      const dev = await authService.getCurrentDeveloper();
      if (dev) {
        setDeveloper(dev);
        return true;
      }

      setError('Failed to load developer profile. Your email may not have a linked developer account.');
      return false;
    } catch {
      setError('An unexpected error occurred');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const completeNewPassword = useCallback(async (newPassword: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await authService.completeNewPassword(newPassword);

      if (!result.success) {
        setError(result.error || 'Password change failed');
        return false;
      }

      setNeedsNewPassword(false);

      // Admin users may not have a DynamoDB developer record
      if (authService.isAdmin()) {
        const stub = authService.getAdminStubDeveloper();
        if (stub) {
          setDeveloper(stub);
          return true;
        }
      }

      // Regular developer — fetch profile from API
      const dev = await authService.getCurrentDeveloper();
      if (dev) {
        setDeveloper(dev);
        return true;
      }

      setError('Failed to load developer profile.');
      return false;
    } catch {
      setError('An unexpected error occurred');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    authService.logout();
    setDeveloper(null);
    setNeedsNewPassword(false);
    setError(null);
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const isAdmin = useMemo(() => authService.isAdmin(), [developer]);

  return (
    <AuthContext.Provider
      value={{
        developer,
        isAuthenticated: !!developer,
        isLoading,
        isAdmin,
        needsNewPassword,
        login,
        completeNewPassword,
        logout,
        error,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
